import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { formatAssistantText } from "./formatAssistantText";
import AssistantMessageBlocks, {
  type AssistantPlanUI,
  shouldRenderBlocksBeforeText,
  shouldRenderBlocksAfterText,
} from "./AssistantMessageBlocks";

type ChatMsg = {
  role: "user" | "assistant";
  content: string;
  sentAt: number;
  planSnapshot?: AssistantPlanUI;
};

export const WELCOME_NEED = "¿Qué le quieres recordar a tus clientes?";
export const WELCOME_CONNECTIONS =
  "Antes de comenzar, revisemos tus conexiones. Usa los botones de abajo si falta algo.";

export interface CampaignAssistantChatHandle {
  initSession: () => Promise<string>;
}

export interface CampaignAssistantChatProps {
  active?: boolean;
  compact?: boolean;
  fullPage?: boolean;
  onCampaignCreated?: (campaignId: string) => void;
  onContactsAdded?: () => void;
}

const CampaignAssistantChat = forwardRef<CampaignAssistantChatHandle, CampaignAssistantChatProps>(
  function CampaignAssistantChat(
    { active = true, compact = false, fullPage = false, onCampaignCreated, onContactsAdded },
    ref
  ) {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [plan, setPlan] = useState<AssistantPlanUI>({});
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);
    const messagesScrollRef = useRef<HTMLDivElement>(null);
    const sessionInitialized = useRef(false);
    const prevMsgCount = useRef(0);

    // Scroll only the messages pane (not the page) when new messages arrive
    useEffect(() => {
      if (messages.length <= prevMsgCount.current && !loading) {
        prevMsgCount.current = messages.length;
        return;
      }
      prevMsgCount.current = messages.length;
      const el = messagesScrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, [messages.length, loading]);

    const initSession = useCallback(async () => {
      const res = await api.post("/agent/session");
      const newPlan: AssistantPlanUI = res.data.plan || {};
      setSessionId(res.data.session_id);
      setPlan(newPlan);
      const missing = newPlan.missing_connections || [];
      const welcome =
        missing.length > 0
          ? WELCOME_CONNECTIONS
          : WELCOME_NEED;
      setMessages([{ role: "assistant", content: welcome, sentAt: Date.now(), planSnapshot: newPlan }]);
      sessionInitialized.current = true;
      return res.data.session_id as string;
    }, []);

    useImperativeHandle(ref, () => ({ initSession }), [initSession]);

    useEffect(() => {
      if (!active || sessionInitialized.current) return;
      initSession().catch(() => toast.error("No se pudo iniciar la sesión del asistente"));
    }, [active, initSession]);

    const refreshConnections = useCallback(async () => {
      if (!sessionId) return;
      const requestSentAt = Date.now();
      try {
        const res = await api.post("/agent/chat", {
          session_id: sessionId,
          messages: [
            ...messages.map(({ role, content }) => ({ role, content })),
            { role: "user", content: "Ya conecté, revisa de nuevo por favor" },
          ],
        });
        const reply = formatAssistantText(res.data.reply || "");
        const newPlan: AssistantPlanUI = res.data.plan || plan;
        setMessages((prev) => [
          ...prev,
          { role: "user", content: "Ya conecté, revisa de nuevo por favor", sentAt: requestSentAt },
          { role: "assistant", content: reply, sentAt: Date.now(), planSnapshot: newPlan },
        ]);
        if (res.data.plan) setPlan(res.data.plan);
      } catch {
        toast.error("No se pudo actualizar el estado de conexiones");
      }
    }, [sessionId, messages, plan]);

    const chatLocked = Boolean(plan.campaign_id);

    const goToCampaigns = useCallback(() => {
      window.location.href = "/renewals";
    }, []);

    const sendMessage = async () => {
      const text = input.trim();
      if (!text || loading || chatLocked) return;
      const sid = sessionId || (await initSession());
      const nextMessages = [...messages, { role: "user" as const, content: text, sentAt: Date.now() }];
      setMessages(nextMessages);
      setInput("");
      setLoading(true);
      try {
        const res = await api.post("/agent/chat", {
          session_id: sid,
          messages: nextMessages.map(({ role, content }) => ({ role, content })),
        });
        const reply = formatAssistantText(res.data.reply || "");
        const newPlan: AssistantPlanUI = res.data.plan || plan;
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: reply, sentAt: Date.now(), planSnapshot: newPlan },
        ]);
        if (res.data.plan) {
          setPlan(res.data.plan);
        }
        const tools: string[] = res.data.tools_used || [];
        if (tools.includes("create_campaign")) {
          const campaignId = res.data.plan?.campaign_id || newPlan.campaign_id;
          if (campaignId && onCampaignCreated) {
            onCampaignCreated(campaignId);
          } else {
            toast.success("Campaña creada");
          }
        }
        if (tools.includes("add_sheet_contacts")) {
          toast.success("Contactos agregados a la hoja");
          onContactsAdded?.();
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          "Error al enviar mensaje";
        toast.error(String(msg));
      } finally {
        setLoading(false);
      }
    };

    const messagesAreaClass = fullPage
      ? "flex-1 overflow-y-auto overflow-x-hidden space-y-3 sm:space-y-4 px-0.5 sm:px-1 min-h-0 overscroll-contain"
      : compact
        ? "flex-1 overflow-y-auto space-y-3 pr-1 min-h-0"
        : "flex-1 overflow-y-auto space-y-3 pr-1 min-h-[400px]";

    const containerClass = fullPage
      ? "flex flex-col flex-1 min-h-0 h-full w-full"
      : compact
        ? "flex flex-col h-full min-h-0 gap-3"
        : "flex flex-col min-h-[calc(100vh-10rem)] gap-3";

    return (
      <div className={containerClass}>
        <div ref={messagesScrollRef} className={messagesAreaClass}>
          {messages.map((m, i) => {
            const isLastAssistant =
              m.role === "assistant" && i === messages.length - 1 && !loading;
            const blocksPlan = isLastAssistant ? plan : m.planSnapshot;

            return (
            <div
              key={i}
              className={`w-full flex flex-col gap-2 ${
                m.role === "user" ? "items-end" : "items-start"
              }`}
            >
              {m.role === "assistant" &&
                blocksPlan &&
                shouldRenderBlocksBeforeText(blocksPlan.conversation_step) && (
                  <div className="w-full">
                    <AssistantMessageBlocks
                      plan={blocksPlan}
                      onConnectionsUpdated={refreshConnections}
                      onGoToCampaigns={goToCampaigns}
                    />
                  </div>
                )}
              <div
                className={`w-fit max-w-[90%] md:max-w-[min(75%,680px)] rounded-2xl px-4 py-2.5 shadow-sm border break-words text-[14px] leading-relaxed whitespace-pre-wrap text-gray-800 ${
                  m.role === "user"
                    ? "bg-[#d9fdd3] rounded-tr-none border-[#c1f5b6]"
                    : "bg-white rounded-tl-none border-gray-200"
                }`}
              >
                {m.role === "assistant" ? formatAssistantText(m.content) : m.content}
                <div className="mt-1 flex justify-end text-[10px] leading-none text-gray-500">
                  {new Date(m.sentAt)
                    .toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })
                    .replace(" AM", "am")
                    .replace(" PM", "pm")}
                </div>
              </div>
              {m.role === "assistant" &&
                blocksPlan &&
                (shouldRenderBlocksAfterText(blocksPlan.conversation_step) ||
                  !shouldRenderBlocksBeforeText(blocksPlan.conversation_step)) && (
                  <div className="w-full">
                    <AssistantMessageBlocks
                      plan={blocksPlan}
                      onConnectionsUpdated={refreshConnections}
                      onGoToCampaigns={goToCampaigns}
                    />
                  </div>
                )}
            </div>
            );
          })}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 mr-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Escribiendo...
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div className={`shrink-0 w-full ${fullPage ? "mt-3" : "mt-2"}`}>
          <div className="flex items-center gap-2 w-full rounded-full border border-gray-200 bg-white pl-5 pr-2 py-2 shadow-sm focus-within:ring-2 focus-within:ring-teal-500/40">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                chatLocked
                  ? "Campaña creada — el chat está cerrado"
                  : "Escribe tu mensaje..."
              }
              disabled={loading || chatLocked}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-gray-800 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            />
            <Button
              onClick={sendMessage}
              disabled={loading || chatLocked || !input.trim()}
              className="h-8 w-8 shrink-0 rounded-full !bg-teal-600 hover:!bg-teal-500 text-white shadow-none disabled:opacity-30"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

export default CampaignAssistantChat;
